/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { ApiStatus } from '../../../../ts';
import type {
  Assistant,
  Environment,
  SkillReference
} from '../../../../ts/assistant';
import { Language, SkillType } from '../../../../ts/skill';
import type { RootState } from '../../../../ts/state';
import { selectSkillLanguageById } from '../skills/core-selectors';
import { entityAdapter } from './adapter';

/**
 * Module that contains all selectors that do NOT rely on other slice's selectors (to avoid injecting cyclical
 * module dependencies).
 */

const selectors = entityAdapter.getSelectors(
  (state: RootState) => state.assistants
);

export const selectAllAssistants = selectors.selectAll;

export const selectAssistantById = selectors.selectById;

export const selectAllAssistantIds = (state: RootState): string[] =>
  state.assistants.ids as string[];

export const areAssistantsLoaded = (state: RootState): boolean =>
  state.assistants.listStatus === ApiStatus.SUCCEEDED;

export const selectAssistant = (state: RootState): Assistant | undefined =>
  selectAssistantById(state, state?.session?.assistantId);

export const selectAssistantLanguage = (state: RootState): Language | null => {
  const assistant = selectAssistant(state);
  if (!assistant) {
    return null;
  }

  if (assistant?.language) {
    return assistant.language;
  }

  const references = selectSkillReferencesFromAssistant(assistant);
  if (!references) {
    return null;
  }
  // just pick the language from the first one.
  return selectSkillLanguageById(state, references[0]?.skill_id) || null;
};

export const hasAssistants = (state: RootState) =>
  selectors.selectTotal(state) > 0;

const sanitizeUrl = (url: string): string => {
  url = url.replace('/v1', '');
  url = url.replace('/v2', '');
  if (url.charAt(url.length - 1) === '/') {
    url = url.substr(0, url.length - 1);
  }
  return url;
};

export const getAssistantUrl = (url: string, assistantId: string) =>
  `${sanitizeUrl(url)}/v2/assistants/${assistantId}/sessions`;

export const getServiceUrl = (url: string, instanceId: string): string =>
  `${sanitizeUrl(url)}/instances/${instanceId}`;

export const getSkillUrl = (url: string, workspaceId: string) =>
  `${sanitizeUrl(url)}/v1/workspaces/${workspaceId}/message`;

export const selectSkillReferencesFromAssistant = (
  assistant: Assistant | Environment
): SkillReference[] | undefined => {
  return (
    assistant?.skill_references || (assistant as Assistant)?.assistant_skills
  );
};

export const selectDialogSkillIdFromAssistant = (
  state: RootState,
  assistantId: string
): string | undefined => {
  const assistant = selectAssistantById(state, assistantId);
  if (selectSkillReferencesFromAssistant(assistant)) {
    const reference = selectSkillReferencesFromAssistant(assistant).find(
      skill => skill.type === SkillType.DIALOG
    );
    return reference?.skill_id ?? undefined;
  }
  return undefined;
};

export const selectDialogSkillId = (state: RootState): string | undefined => {
  const assistantId = state.session?.assistantId;
  return selectDialogSkillIdFromAssistant(state, assistantId);
};

export const selectActionSkillIdFromAssistant = (
  state: RootState,
  assistantId: string
): string | undefined => {
  const assistant = selectAssistantById(state, assistantId);
  if (selectSkillReferencesFromAssistant(assistant)) {
    const reference = selectSkillReferencesFromAssistant(assistant).find(
      skill => skill.type === SkillType.ACTION
    );
    return reference?.skill_id ?? undefined;
  }
  return undefined;
};

export const selectActionSkillId = (state: RootState): string | undefined => {
  // To remove the cyclical dependency between the session and skill selectors,
  // directly access the "assistantId" property of the "session" slice.
  const assistantId = state.session?.assistantId;

  return selectActionSkillIdFromAssistant(state, assistantId);
};
